/**
 * "Install the app" support (Sep 2026). Android/Chromium fires
 * `beforeinstallprompt` once, early — we capture it at module load (imported
 * from main.tsx) so the Home card can offer a one-tap Install. iOS has no such
 * event: the card shows the Share → Add to Home Screen steps instead.
 */
import { isStandalone } from '../applock/appLock';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

export const canOneTapInstall = () => deferred !== null;
export function onInstallAvailabilityChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
/** Shows the browser's own install dialog; resolves true when the user accepted. */
export async function oneTapInstall(): Promise<boolean> {
  if (!deferred) return false;
  const e = deferred;
  deferred = null;
  await e.prompt();
  const choice = await e.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
  listeners.forEach((l) => l());
  return choice.outcome === 'accepted';
}

export function isIosDevice(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}
/** iOS in-app browsers (Gmail, Slack, Chrome on iOS) can't add to Home Screen — only Safari can. */
export function isIosNonSafari(): boolean {
  const ua = navigator.userAgent;
  return isIosDevice() && /CriOS|FxiOS|EdgiOS|GSA\/|Instagram|FBAN|FBAV|Slack|Gmail/i.test(ua);
}
export function isPhoneOrTablet(): boolean {
  return isIosDevice() || /Android/i.test(navigator.userAgent) || (window.matchMedia?.('(pointer: coarse)').matches ?? false);
}
/** Show the install card: a phone/tablet browser tab (not the installed app). */
export function shouldOfferInstall(): boolean {
  return typeof window !== 'undefined' && !isStandalone() && isPhoneOrTablet();
}

// Per-browser, per-user dismissal (localStorage). "Not now" hides it for 14 days.
const key = (kind: 'install' | 'push', email: string) => `daaily.prompt.${kind}:${email.toLowerCase()}`;
export function promptDismissed(kind: 'install' | 'push', email: string): boolean {
  try {
    const until = Number(localStorage.getItem(key(kind, email)) || 0);
    return until > Date.now();
  } catch { return false; }
}
export function dismissPrompt(kind: 'install' | 'push', email: string, days: number): void {
  try { localStorage.setItem(key(kind, email), String(Date.now() + days * 86_400_000)); } catch { /* ignore */ }
}
